import {useEffect} from 'react'
import {useIntersectionObserver} from 'usehooks-ts'

import {twMerge} from '@sdox/utils'
import {RichText} from '@sdox/ui'

import {useTocStore} from '../../stores'

import type {ComponentProps} from 'react'

export interface PageHeaderProps extends ComponentProps<'div'> {
  callout?: string
  calloutProps?: Omit<ComponentProps<'h2'>, 'children'>
  title?: string
  titleProps?: Omit<ComponentProps<'h2'>, 'children'>
  subTitle?: string
  subTitleProps?: Omit<ComponentProps<'p'>, 'children'>
}

export const PageHeader = ({
  callout, calloutProps,
  title, titleProps,
  subTitle, subTitleProps,
  children,
  ...restProps
}: PageHeaderProps) => {
  // ignore the bottom half of the screen for interPages. This reduces false highlighting of headings
  //  that are below other headings
  const {isIntersecting, ref} = useIntersectionObserver({rootMargin: '0% 0% -50% 0px', threshold: 0.5})
  const setActiveAnchor = useTocStore(state => state.setActiveAnchor)

  useEffect(() => {
    if (isIntersecting && restProps.id) setActiveAnchor(restProps.id)
  }, [isIntersecting, restProps.id, setActiveAnchor])

  return (
    <div
      className={'@container/block'}
    >
      <div
        ref={ref}
        {...restProps}
        className={twMerge(
          title || subTitle || callout ? 'mb-spacing-48 @block-medium:mb-block-large @block-large:mb-[9.375cqw]' : '',
          // on small screens the scroll margin is 128px accounts for page padding + mobile top nav
          // for bigger screens/container it's the Page item padding (12.5cqw).
          'scroll-mt-spacing-128 @block-medium:scroll-mt-[12.5cqw]',
          restProps.className,
        )}
      >
        {children}
        {callout && (
          <RichText
            as={'p'}
            content={callout}
            allowedTags={['span']}
            className={
              twMerge(
                'mb-spacing-12 text-balance text-p1',
                calloutProps?.className,
              )
            }
          />
        )}
        {title && (
          <h2
            className={twMerge(
              'text-balance text-h2-xs @block-large:text-h2',
              subTitle && 'mb-spacing-12',
              titleProps?.className,
            )}
          >
            <RichText
              content={title}
              allowedTags={['span']}
            />
          </h2>
        )}
        {subTitle && (
          <RichText
            as={'h3'}
            content={subTitle}
            allowedTags={['span']}
            className={
              twMerge(
                'text-balance text-p1 text-glyph-default-secondary @block-large:text-h3',
                subTitleProps?.className,
              )
            }
          />
        )}
      </div>
    </div>
  )
}
